import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

import { CitationMetadata, TextWithCitations, processContentWithCitations } from "components/citation-utils/citation-utils";

// Sanitization rules for the HTML inside an answer. Video and image tags are allowed because an
// answer can embed media from the knowledge base. Every other HTML tag is removed: the text is
// written by a model reading external documents and it is injected into the user's page.
const SANITIZE_SCHEMA = {
	...defaultSchema,
	tagNames: [...(defaultSchema.tagNames || []), "video", "source", "img"],
	attributes: {
		...(defaultSchema.attributes || {}),
		video: ["controls", "width", "height", "src"],
		source: ["src", "type"],
		img: ["src", "alt", "width", "height"]
	}
};

// Markdown tags that contain text. Citation markers only appear inside these tags, so only these
// are rendered through the citation wrapper.
const CITATION_WRAPPED_TAGS = ["p", "li", "h1", "h2", "h3", "code"] as const;

const buildComponents = (
	citationMap: ReturnType<typeof processContentWithCitations>["citationMap"],
	onImageClick?: (src: string) => void
): Components => {
	const wrapWithCitations =
		(tag: string) =>
		({ children }: { children?: React.ReactNode }) =>
			React.createElement(tag, null, <TextWithCitations citationMap={citationMap}>{children}</TextWithCitations>);

	// Built as a plain record and cast at the end: indexing the Components union by tag name is too
	// complex for TypeScript.
	const components: Record<string, unknown> = {
		img: ({ src, alt, ...props }: any) => (
			<img
				src={src}
				alt={alt}
				{...props}
				style={onImageClick ? { cursor: "pointer" } : undefined}
				onClick={() => src && onImageClick?.(src)}
			/>
		),
		a: ({ href, children, ...props }: any) => (
			<a href={href} target="_blank" rel="noreferrer" {...props}>
				{children}
			</a>
		)
	};

	for (const tag of CITATION_WRAPPED_TAGS) {
		components[tag] = wrapWithCitations(tag);
	}

	return components as Components;
};

interface ChatMarkdownProps {
	content: string;
	// Web sources used to write this answer. The markers they leave in the text are replaced with
	// numbered chips that the user can hover.
	citations?: CitationMetadata | null;
	onImageClick?: (src: string) => void;
}

// Renders an AI answer as markdown, with its citations resolved and its embedded media clickable.
const ChatMarkdown: React.FC<ChatMarkdownProps> = ({ content, citations, onImageClick }) => {
	const { modifiedContent, citationMap } = useMemo(
		() => processContentWithCitations(content, citations ?? undefined),
		[content, citations]
	);

	const components = useMemo(() => buildComponents(citationMap, onImageClick), [citationMap, onImageClick]);

	return (
		<div className="chat-markdown">
			<ReactMarkdown
				remarkPlugins={[remarkGfm]}
				rehypePlugins={[rehypeRaw, [rehypeSanitize, SANITIZE_SCHEMA]]}
				components={components}
			>
				{modifiedContent}
			</ReactMarkdown>
		</div>
	);
};

export default ChatMarkdown;
